import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  DiscountCode,
  DiscountCodeCreatePayload,
  DiscountCodeType,
  DiscountCodeUpdatePayload,
} from '../../../models/discountCode';

export interface DiscountCodeCompanyOption {
  companyId: string;
  companyName: string;
}

export interface DiscountCodeFormModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  /** Existing code when editing. */
  initial?: DiscountCode | null;
  /** Companies available for assignment. */
  companies: DiscountCodeCompanyOption[];
  onClose: () => void;
  onCreate: (payload: DiscountCodeCreatePayload) => Promise<void>;
  onUpdate: (id: string, payload: DiscountCodeUpdatePayload) => Promise<void>;
}

interface FormState {
  code: string;
  type: DiscountCodeType;
  amount: string;
  expiresAt: string;
  companyIds: string[];
  stripeCouponId: string;
}

function toDateInputValue(iso: string | null): string {
  if (!iso) {
    return '';
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
}

function buildInitialState(mode: 'create' | 'edit', initial?: DiscountCode | null): FormState {
  if (mode === 'edit' && initial) {
    return {
      code: initial.code,
      type: initial.type,
      amount: String(initial.amount),
      expiresAt: toDateInputValue(initial.expiresAt),
      companyIds: initial.assignedCompanies.map((c) => c.companyId),
      stripeCouponId: initial.stripeCouponId ?? '',
    };
  }
  return { code: '', type: 'percentage', amount: '', expiresAt: '', companyIds: [], stripeCouponId: '' };
}

export function DiscountCodeFormModal({
  visible,
  mode,
  initial,
  companies,
  onClose,
  onCreate,
  onUpdate,
}: DiscountCodeFormModalProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>(() => buildInitialState(mode, initial));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (visible) {
      setForm(buildInitialState(mode, initial));
      setFieldErrors({});
      setError(null);
      setPending(false);
    }
  }, [visible, mode, initial]);

  const title = useMemo(() => (mode === 'create' ? 'New discount code' : 'Edit discount code'), [mode]);

  if (!visible) {
    return null;
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (mode === 'create' && !form.code.trim()) {
      next.code = 'A code is required.';
    }
    const amount = Number(form.amount);
    if (!form.amount.trim() || Number.isNaN(amount) || amount <= 0) {
      next.amount = 'Amount must be greater than zero.';
    } else if (form.type === 'percentage' && amount > 100) {
      next.amount = 'Percentage discounts cannot exceed 100.';
    }
    if (form.companyIds.length === 0) {
      next.companyIds = 'Assign at least one company.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (!validate()) {
      return;
    }
    setPending(true);
    try {
      const amount = Number(form.amount);
      const expiresAt = form.expiresAt ? new Date(`${form.expiresAt}T00:00:00.000Z`).toISOString() : null;
      const stripeCouponId = form.stripeCouponId.trim();
      if (mode === 'create') {
        await onCreate({
          code: form.code.trim(),
          type: form.type,
          amount,
          expiresAt,
          companyIds: form.companyIds,
          stripeCouponId: stripeCouponId || null,
        });
      } else if (initial) {
        await onUpdate(initial.id, {
          type: form.type,
          amount,
          expiresAt,
          companyIds: form.companyIds,
          stripeCouponId,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the discount code.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="bo-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!pending) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-discount-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-discount-modal-title" className="bo-modal-title">
          {title}
        </h2>
        <form className="bo-form-fields" onSubmit={(e) => void handleSubmit(e)}>
          <label className="bo-field" htmlFor="discount-code">
            <span className="bo-field-label">Code</span>
            <input
              id="discount-code"
              className="bo-input"
              value={form.code}
              disabled={mode === 'edit'}
              onChange={(e) => setField('code', e.target.value)}
              placeholder="e.g. SUMMER10"
            />
            {fieldErrors.code ? (
              <span className="bo-field-error" role="alert">
                {fieldErrors.code}
              </span>
            ) : null}
          </label>

          <label className="bo-field" htmlFor="discount-type">
            <span className="bo-field-label">Type</span>
            <select
              id="discount-type"
              className="bo-input"
              value={form.type}
              onChange={(e) => setField('type', e.target.value as DiscountCodeType)}
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed</option>
            </select>
          </label>

          <label className="bo-field" htmlFor="discount-amount">
            <span className="bo-field-label">Amount</span>
            <input
              id="discount-amount"
              className="bo-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              placeholder={form.type === 'percentage' ? '0 - 100' : 'Amount off'}
            />
            {fieldErrors.amount ? (
              <span className="bo-field-error" role="alert">
                {fieldErrors.amount}
              </span>
            ) : null}
          </label>

          <label className="bo-field" htmlFor="discount-expires">
            <span className="bo-field-label">Expiration date</span>
            <input
              id="discount-expires"
              className="bo-input"
              type="date"
              value={form.expiresAt}
              onChange={(e) => setField('expiresAt', e.target.value)}
            />
          </label>

          <label className="bo-field" htmlFor="discount-companies">
            <span className="bo-field-label">Assigned companies</span>
            <select
              id="discount-companies"
              className="bo-input"
              multiple
              size={Math.min(6, Math.max(3, companies.length))}
              value={form.companyIds}
              onChange={(e) =>
                setField(
                  'companyIds',
                  Array.from(e.target.selectedOptions).map((option) => option.value)
                )
              }
            >
              {companies.map((company) => (
                <option key={company.companyId} value={company.companyId}>
                  {company.companyName || company.companyId}
                </option>
              ))}
            </select>
            {fieldErrors.companyIds ? (
              <span className="bo-field-error" role="alert">
                {fieldErrors.companyIds}
              </span>
            ) : null}
          </label>

          <label className="bo-field" htmlFor="discount-stripe">
            <span className="bo-field-label">Stripe coupon ID (optional)</span>
            <input
              id="discount-stripe"
              className="bo-input"
              value={form.stripeCouponId}
              onChange={(e) => setField('stripeCouponId', e.target.value)}
              placeholder="e.g. coupon_ABC123"
            />
          </label>

          {error ? (
            <p className="bo-modal-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose} disabled={pending}>
              Cancel
            </button>
            <button type="submit" className="bo-btn bo-btn-primary" disabled={pending}>
              {pending ? 'Saving…' : mode === 'create' ? 'Create code' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
